import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@stores/gameStore';

/**
 * Connection status indicator
 * Shows current WebSocket connection state
 */
export function ConnectionStatus(): React.JSX.Element {
  const { connectionStatus, connectionError } = useGameStore();

  // Determine display state
  const getStatusConfig = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          color: 'bg-aviator-success',
          text: 'Connected',
          visible: false, // Hide when connected
        };
      case 'connecting':
        return {
          color: 'bg-aviator-gold',
          text: 'Connecting...',
          visible: true,
        };
      case 'reconnecting':
        return {
          color: 'bg-aviator-gold',
          text: 'Reconnecting...',
          visible: true,
        };
      case 'disconnected':
        return {
          color: 'bg-aviator-danger',
          text: 'Disconnected',
          visible: true,
        };
      case 'error':
        return {
          color: 'bg-aviator-danger',
          text: connectionError ?? 'Connection Error',
          visible: true,
        };
      default:
        return {
          color: 'bg-gray-500',
          text: 'Unknown',
          visible: false,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <AnimatePresence>
      {config.visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-4 right-4 z-50"
        >
          <div
            className={`flex items-center gap-2 rounded-full px-4 py-2 shadow-lg ${config.color}`}
          >
            {/* Status dot */}
            <motion.div
              animate={
                connectionStatus === 'connecting' || connectionStatus === 'reconnecting'
                  ? { scale: [1, 1.2, 1] }
                  : {}
              }
              transition={{ repeat: Infinity, duration: 1 }}
              className="h-2 w-2 rounded-full bg-white"
            />
            <span className="text-sm font-medium text-white">{config.text}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
